import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { formatCurrency } from "../config/market";
import { minimumOrderMessage, minimumOrderExplainer } from "../utils/minimumOrder";
import { trackMinOrderReached } from "../utils/analytics";

/**
 * MinimumOrderNotice: the ONE way every cart surface shows how close a basket
 * is to the minimum order, while the shopper is still in the cart and a tap
 * away from adding more.
 *
 * A progress bar rather than a warning: the closer a goal looks, the harder
 * people work to finish it (the goal-gradient effect), so the bar fills as the
 * basket grows and turns into "Minimum order reached" when it clears. Reads a
 * shortfall computed by utils/minimumOrder; the server remains the real gate.
 *
 * Pass `subtotal` to get the reached state; without it the notice keeps its
 * old behaviour and shows only while something is missing. An empty cart shows
 * nothing.
 */
const MinimumOrderNotice = ({
  shortfall,
  minimum,
  subtotal,
  vendorName,
  vendorType,
  surface = "cart",
  format = formatCurrency,
  /* The hub stacks one of these per vendor group, where the surprising part is
     not the number but that each vendor needs its own. */
  perVendor = false,
  variant = "inline", // 'inline' | 'block'
  className,
  style,
}) => {
  const gap = Number(shortfall);
  const floor = Number(minimum);
  const goods = Number(subtotal);
  const isShort = gap > 0;
  const hasFloor = Number.isFinite(floor) && floor > 0;
  const isReached = !isShort && hasFloor && Number.isFinite(goods) && goods > 0;

  /* Count the moment the bar fills, not every render after it. */
  const wasShort = useRef(isShort);
  useEffect(() => {
    if (wasShort.current && isReached) {
      trackMinOrderReached({ surface, vendorType, subtotal: goods });
    }
    wasShort.current = isShort;
  }, [isShort, isReached, surface, vendorType, goods]);

  if (!isShort && !isReached) return null;

  const progress = isReached ? 100 : hasFloor ? Math.max(4, Math.min(100, ((floor - gap) / floor) * 100)) : 0;

  return (
    <Notice
      $variant={variant}
      $reached={isReached}
      className={className}
      style={style}
      role="status"
    >
      <Message>
        {isReached ? (
          <CheckCircleOutlineIcon fontSize="small" aria-hidden="true" />
        ) : (
          <AddShoppingCartIcon fontSize="small" aria-hidden="true" />
        )}
        <Text>
          {vendorName ? <b>{vendorName}: </b> : null}
          {isReached ? (
            "Minimum order reached"
          ) : (
            <>
              {minimumOrderMessage(gap, format)}. {minimumOrderExplainer(minimum, format)}
              {perVendor ? " Each vendor is delivered separately, so each one has to reach it on its own." : ""}
            </>
          )}
        </Text>
      </Message>
      {hasFloor && (
        <Track
          role="progressbar"
          aria-label="Progress to the minimum order"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress)}
        >
          <Fill style={{ width: `${progress}%` }} $reached={isReached} />
        </Track>
      )}
    </Notice>
  );
};

const Notice = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: ${(p) => (p.$variant === "block" ? "14px 16px" : "10px 12px")};
  border-radius: 10px;
  margin: ${(p) => (p.$variant === "block" ? "12px 0" : "6px 0")};
  font-size: 14px;
  line-height: 1.4;
  text-align: left;
  border: 1px solid ${(p) => (p.$reached ? "#cfe3b0" : "#f0d9a8")};
  background: ${(p) => (p.$reached ? "#f1f8e8" : "#fdf7e8")};
  color: ${(p) => (p.$reached ? "#2c4d08" : "#7a4e07")};

  b {
    font-weight: 700;
  }
`;

const Message = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;

  svg {
    margin-top: 1px;
    flex-shrink: 0;
  }
`;

const Text = styled.span`
  flex: 1;
`;

const Track = styled.div`
  height: 6px;
  border-radius: 999px;
  background: rgba(21, 35, 42, 0.1);
  overflow: hidden;
`;

const Fill = styled.div`
  height: 100%;
  border-radius: 999px;
  background: ${(p) => (p.$reached ? "#3d6a0b" : "#c27c0e")};
  transition: width 240ms ease-out;
`;

export default MinimumOrderNotice;
